import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

import { hasValidAdminUnitRouteId } from "../../../pages/admin/uebersicht/uebersichtHelpers";
import { money, pct, selectRankingItems } from "./performanceSelectors";

/**
 * The units of the selected month, ordered by Objektmarge — the server's order.
 *
 * NOTHING HERE SORTS. `selected_ranking.items` arrives with the R2 chain already
 * applied and each row carrying its own `rank`. Re-deciding the order in a
 * browser is how a ranking and the figures printed beside it drift apart: the
 * client would rank on the value it happens to read, the server on the one it
 * actually ranked by, and the two are only the same until a tie-break or a null
 * margin appears. `performanceGuards.test.js` keeps `.sort(` out of this folder.
 *
 * THE MARGIN IS THE HEADLINE BECAUSE IT IS THE CRITERION. Franc figures follow it
 * on the same row so a reader can see WHY a unit ranks where it does — a large
 * unit with a thin margin sits below a small one with a fat margin, which looks
 * wrong until the two revenue figures are visible.
 *
 * A NULL MARGIN IS RENDERED AS "—", NEVER 0 %. The contract publishes null when
 * revenue is not positive: such a unit has no measurable margin, while "0 %"
 * would read as break-even for a unit that is in fact losing its whole cost.
 *
 * NO BAR. The repo has no established visual language for a signed, diverging
 * scale, and a margin is signed: a bar whose width is the margin would draw
 * −40 % and +40 % identically, and one anchored at zero would need a maximum
 * this contract does not define. The number is the information.
 */

const CARD = "rounded-xl border border-border bg-card p-4 shadow-sm";

function CardShell({ subtitle, children }) {
  return (
    <section
      data-testid="performance-ranking-card"
      aria-labelledby="performance-ranking-heading"
      className={CARD}
    >
      <h2
        id="performance-ranking-heading"
        className="m-0 text-[13px] font-semibold text-card-foreground"
      >
        Performance-Ranking (Objektmarge)
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{subtitle}</p>
      {children}
    </section>
  );
}

export default function PerformanceRankingCard({
  ranking,
  stand = "",
  isRange = false,
  loading = false,
}) {
  // P2: "im ausgewählten Monat" is false over a window, and `stand` already
  // names the period — so the range form drops the month wording entirely
  // rather than contradicting the suffix beside it.
  const subtitle = isRange
    ? `Nach Objektmarge${stand}`
    : `Nach Objektmarge im ausgewählten Monat${stand}`;

  if (loading) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </CardShell>
    );
  }

  const items = selectRankingItems(ranking);

  if (items.length === 0) {
    return (
      <CardShell subtitle={subtitle}>
        <p className="mt-4 text-[12px] text-muted-foreground">Keine Einheiten im Ranking.</p>
      </CardShell>
    );
  }

  return (
    <CardShell subtitle={subtitle}>
      <ul
        data-testid="performance-ranking-rows"
        className="mt-3 list-none divide-y divide-border p-0"
      >
        {items.map((row) => {
          const label = row.unit_label || "Unbekannte Einheit";
          const body = (
            <>
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground">
                  {row.rank}.
                </span>
                <span className="min-w-0">
                  {/* `break-words`, not `truncate`: a long unit label in a narrow
                      column is still the row's identity, and cutting it off is
                      how two units become indistinguishable. */}
                  <span className="block break-words text-[12px] font-medium text-card-foreground">
                    {label}
                  </span>
                  <span className="mt-0.5 block break-words text-[10px] tabular-nums text-muted-foreground">
                    Umsatz {money(row.expected_recurring_revenue_chf)} · Objektergebnis{" "}
                    {money(row.operating_result_chf)}
                  </span>
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span
                  data-testid={`performance-ranking-margin-${row.unit_id}`}
                  className="block text-[16px] font-semibold tabular-nums text-card-foreground"
                >
                  {pct(row.operating_margin_pct)}
                </span>
                <span className="mt-0.5 block text-[10px] tabular-nums text-muted-foreground">
                  Auslastung {pct(row.monthly_occupancy_pct)}
                </span>
              </span>
            </>
          );

          const linkable = hasValidAdminUnitRouteId({ id: row.unit_id });

          return (
            <li key={row.unit_id ?? row.rank} className="py-2 first:pt-0 last:pb-0">
              {linkable ? (
                <Link
                  to={`/admin/units/${encodeURIComponent(String(row.unit_id))}`}
                  data-testid={`performance-ranking-unit-${row.unit_id}`}
                  className="flex items-start justify-between gap-3 rounded-[6px] px-1 py-0.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {body}
                </Link>
              ) : (
                <div className="flex items-start justify-between gap-3 px-1 py-0.5">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </CardShell>
  );
}
